import openai from './openaiClient';
import {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Generates a personalized message using AI
 * @param {Object} params - Message generation parameters
 * @returns {Promise<string>} Generated message
 */
export async function generateMessage(params) {
  const { recipientName, messageType, context, tone = 'professional', language = 'Arabic' } = params;

  try {
    const systemPrompt = `You are a professional real estate communication assistant. Generate messages in ${language} with a ${tone} tone. Always be respectful, clear, and concise.`;

    const userPrompt = `Generate a ${messageType} message for ${recipientName}. Context: ${context}. Keep it concise and appropriate for ${messageType === 'whatsapp' ? 'WhatsApp' : messageType === 'sms' ? 'SMS (160 characters max)' : 'email'}.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      max_tokens: messageType === 'sms' ? 100 : 500,
      temperature: 0.7,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating message:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates personalized bulk messages for multiple recipients
 * @param {Object} params - Bulk message parameters
 * @returns {Promise<Array>} Array of personalized messages
 */
export async function generateBulkMessages(params) {
  const { recipients, messageTemplate, context, tone = 'professional' } = params;

  try {
    const systemPrompt = `You are a professional real estate communication assistant. Personalize messages in Arabic with a ${tone} tone based on recipient details.`;

    const messages = [];

    for (const recipient of recipients) {
      const userPrompt = `Personalize this message template for ${recipient?.name}: "${messageTemplate}". Recipient details: ${JSON.stringify(recipient)}. Context: ${context}. Keep the core message but add personal touches.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        max_tokens: 300,
        temperature: 0.7,
      });

      messages?.push({
        recipientId: recipient?.id,
        recipientName: recipient?.name,
        message: response?.choices?.[0]?.message?.content,
      });
    }

    return messages;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating bulk messages:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates message templates for different scenarios
 * @param {Object} params - Template generation parameters
 * @returns {Promise<Array>} Array of generated templates
 */
export async function generateTemplates(params) {
  const { category, count = 5, tone = 'professional' } = params;

  try {
    const systemPrompt = `You are a professional real estate communication assistant. Generate ${count} message templates in Arabic for ${category} scenarios with a ${tone} tone.`;

    const userPrompt = `Generate ${count} different message templates for ${category}. Each template should be versatile and include placeholders like [اسم العميل], [اسم العقار], [السعر], etc. Format as JSON array with fields: title, template, placeholders.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'templates_response',
          schema: {
            type: 'object',
            properties: {
              templates: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    template: { type: 'string' },
                    placeholders: { type: 'array', items: { type: 'string' } },
                  },
                  required: ['title', 'template', 'placeholders'],
                },
              },
            },
            required: ['templates'],
            additionalProperties: false,
          },
        },
      },
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.templates;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating templates:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates follow-up reminder suggestions
 * @param {Object} params - Follow-up parameters
 * @returns {Promise<Object>} Follow-up suggestions
 */
export async function generateFollowUpSuggestions(params) {
  const { clientName, lastInteraction, interactionType, context } = params;

  try {
    const systemPrompt = 'You are a professional real estate follow-up assistant. Provide intelligent follow-up suggestions in Arabic.';

    const userPrompt = `Client: ${clientName}. Last interaction: ${lastInteraction} (${interactionType}). Context: ${context}. Suggest: 1) Best follow-up time, 2) Follow-up message, 3) Priority level (high/medium/low), 4) Recommended action. Format as JSON.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'followup_response',
          schema: {
            type: 'object',
            properties: {
              suggestedTime: { type: 'string' },
              message: { type: 'string' },
              priority: { type: 'string' },
              recommendedAction: { type: 'string' },
            },
            required: ['suggestedTime', 'message', 'priority', 'recommendedAction'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating follow-up suggestions:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates communication insights and analytics
 * @param {Object} params - Analytics parameters
 * @returns {Promise<Object>} Communication insights
 */
export async function generateCommunicationInsights(params) {
  const { communicationData, timeRange, metrics } = params;

  try {
    const systemPrompt = 'You are a real estate communication analytics expert. Analyze communication patterns and provide actionable insights in Arabic.';

    const userPrompt = `Analyze this communication data: ${JSON.stringify(communicationData)}. Time range: ${timeRange}. Metrics: ${metrics}. Provide: 1) Key trends, 2) Response rate analysis, 3) Best performing channels, 4) Improvement recommendations, 5) Client engagement insights. Format as JSON.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'insights_response',
          schema: {
            type: 'object',
            properties: {
              keyTrends: { type: 'array', items: { type: 'string' } },
              responseRateAnalysis: { type: 'string' },
              bestChannels: { type: 'array', items: { type: 'string' } },
              recommendations: { type: 'array', items: { type: 'string' } },
              engagementInsights: { type: 'string' },
            },
            required: ['keyTrends', 'responseRateAnalysis', 'bestChannels', 'recommendations', 'engagementInsights'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating communication insights:', error);
    }
    throw new Error(errorInfo.message);
  }
}

export default {
  generateMessage,
  generateBulkMessages,
  generateTemplates,
  generateFollowUpSuggestions,
  generateCommunicationInsights,
};